import { Tabs as AntTabs } from 'antd';
import styled, { css } from 'styled-components';
import { atZoomedDisplaySize } from '../../../assets/styles/components/paneWidth';

const baseTabsStyles = css`
  width: 100%;
  height: 100%;
  .ant-tabs-nav {
    margin-bottom: 0;
    position: relative;
    z-index: 5;
    background: var(--clr-container-bg);
    margin-inline: calc(-1 * var(--spacing-gutter));
    margin-top: 0;
    padding-top: 10px;

    .ant-tabs-nav-operations {
      right: 0px;
      top: 0px;
      width: 32px;
      height: 37px;
      box-shadow: -2px 0px 5px 0px rgba(var(--clr-black-rgb), 0.05);
      background: var(--clr-container-bg);
      position: absolute;
      border-top-right-radius: 10px;
      border-bottom: 1px solid var(--clr-border-grey-100);
      padding: 4px;
      z-index: 2;
      justify-content: center;
      align-items: center;

      .ant-tabs-nav-more {
        width: 24px;
        height: 24px;
        color: var(--clr-general-description);
        padding: 0;
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        .icon {
          font-size: 18px;
        }
        &:hover,
        &[aria-expanded='true'] {
          background: var(--clr-general-hover);
        }
      }
    }

    .ant-tabs-nav-wrap {
      overflow: hidden;
      /* Sideways is the strip's to spend — see useScrollableTabStrip, which drags it by hand —
         and without this claim the host swallows the pan before a pointermove ever arrives.
         Upward stays the browser's, so a swipe that starts on the tabs still scrolls the page. */
      touch-action: pan-y;

      &::before {
        display: none;
      }
      &::after {
        display: none;
      }

      .ant-tabs-nav-list {
        display: flex;
        flex-shrink: 0;
        min-width: 100%;
        /* Zoomed, rc-tabs rounds each tab to whole pixels and a strip that exactly fits reads as overflowing. */
        ${atZoomedDisplaySize(css`
          min-width: calc(100% - 2px);
        `)}
        scrollbar-width: none;
        -ms-overflow-style: none;
        --strip-offset: 0px;
        --strip-glide: 0ms;
        transform: translateX(var(--strip-offset)) !important;
        transition: transform var(--strip-glide) ease-out !important;

        &::-webkit-scrollbar {
          width: 0;
          height: 0;
          background: transparent;
        }
      }
    }
  }

  .ant-tabs-tab {
    padding: 0 10px 10px;
    min-width: fit-content;
    white-space: nowrap;
    justify-content: center;
    width: 100%;

    & + .ant-tabs-tab {
      margin: 0;
    }

    .ant-tabs-tab-btn {
      line-height: 17px;
      font-size: 13px;
      text-shadow: none !important;
      font-weight: 400;
    }
  }

  .ant-tabs-content {
    height: 100%;
    .ant-tabs-tabpane {
      height: 100%;
      padding-top: var(--spacing-gutter);

      &:has(.scroll-page-gutter) {
        padding-top: 0;
      }
    }
  }
`;

export const Tabs = styled(AntTabs)`
  ${baseTabsStyles}
`;

/* Pill variant: the active tab is a filled rounded chip instead of an ink bar. The nav strip
   bleeds to both pane edges so its bottom rule runs edge to edge, and the space either side of
   the chips is then reserved in the two places rc-tabs measures — leading extra content, and the
   overflow button's own box. rc-tabs writes its own scroll offset onto the same node this
   component does, so any inset it cannot measure is one it scrolls away, and the strip jerks as
   the two offsets fight each other. */
export const PillTabs = styled(AntTabs)`
  ${baseTabsStyles}
  > .ant-tabs-nav {
    --pill-row-top: 10px;
    --pill-row-bottom: 10px;
    /* A chip's fill runs to its own edge, so it needs clearance from the overflow button to avoid
       reading as tucked underneath it. The clearance sits inside the button's box rather than
       beside it because rc-tabs measures that box to decide where to park the last chip — a gap it
       cannot see is a gap it scrolls straight through, and the strip then snaps as the two offsets
       fight each other. */
    --pill-overflow-clearance: 10px;

    margin-inline: calc(-1 * var(--spacing-gutter));
    padding-top: var(--pill-row-top);
    padding-bottom: var(--pill-row-bottom);

    /* The overflow button covers the chip row only, so the nav rule stays unbroken beneath it.
       Its box holds the clearance as measured space alone — the scrim that hides chips sliding
       under the button starts where the clearance ends, so the last chip keeps its gap instead
       of ending flush against the scrim's edge. */
    .ant-tabs-nav-operations {
      bottom: var(--pill-row-bottom);
      height: 44px;
      width: calc(32px + var(--pill-overflow-clearance));
      padding: 0 0 0 var(--pill-overflow-clearance);
      display: flex;
      align-items: center;
      border-bottom: none;
      border-top-right-radius: 0;
      justify-content: center;
      background: transparent;
      box-shadow: none;
      /* the clearance is the strip's space, so clicks over it still reach the chip underneath */
      pointer-events: none;

      &::before {
        content: '';
        position: absolute;
        inset: 0 0 0 var(--pill-overflow-clearance);
        z-index: -1;
        background: var(--clr-container-bg);
        box-shadow: -2px 0px 5px 0px rgba(var(--clr-black-rgb), 0.05);
      }

      .ant-tabs-nav-more {
        pointer-events: auto;
      }
    }

    /* CustomTabs renders this as leading extra content so rc-tabs counts it as the strip's
       starting edge rather than as content to be scrolled away. */
    .custom-tabs-strip-gutter {
      display: block;
      width: var(--spacing-gutter);
    }

    .ant-tabs-nav-list {
      align-items: center;
      gap: 5px;
    }

    .ant-tabs-ink-bar {
      display: none;
    }

    /* The active chip's label is the heavier one, so a chip that is sized by its label changes
       width the moment it is selected — and with it the strip's total width. For a strip sitting
       within a couple of pixels of fitting, that is enough to flip rc-tabs' overflow button on
       and off as the selection moves, and the strip lurches with it. Laying the heavier label out
       underneath at zero height reserves that width, so a chip measures the same either way. */
    .custom-tab-label {
      display: inline-block;
      /* the reserved label below pushes this box's baseline down, which would deepen the line it
         sits on; aligning to the top of that line keeps the chip its original height */
      vertical-align: top;

      &::after {
        content: attr(data-label);
        display: block;
        height: 0;
        overflow: hidden;
        visibility: hidden;
        font-weight: 500;
      }
    }

    .ant-tabs-tab {
      width: auto;
      flex: 0 0 auto;
      padding: 4px 10px;
      border-radius: 5px;
      /* the pane swaps in one frame, so a fading chip would still mark the tab that just left */
      transition: none;

      .ant-tabs-tab-btn {
        color: var(--clr-title);
        /* antd transitions every property on this node, and the active chip's heavier label is one
           of them — a weight that ramps over 300ms ramps the chip's width with it and drags the
           whole strip sideways long after the click. The weight lands in one frame instead. */
        transition: none;
      }

      &:not(.ant-tabs-tab-active):not(.ant-tabs-tab-disabled):hover {
        background-color: var(--clr-general-hover);
      }

      &.ant-tabs-tab-active {
        background-color: var(--clr-general-primary);
        .ant-tabs-tab-btn {
          color: var(--clr-white);
          font-weight: 500;
        }
      }
    }
  }
`;

export const WorktimeTab = styled(AntTabs)`
  width: 100%;
  max-width: 100px;
  height: 34px;
  border: 1px solid var(--clr-border-grey-100);
  border-radius: 5px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--clr-general-border-hover);
  }

  .ant-tabs-nav {
    height: 100%;
    margin-bottom: 0;
    &::before {
      display: none;
    }
    .ant-tabs-nav-wrap {
      border-radius: 5px;
    }
    .ant-tabs-nav-list {
      width: 100%;
      .ant-tabs-tab {
        width: 100%;
        font-size: 14px;
        padding: 0;
        justify-content: center;
        background-color: var(--clr-container-bg);
        transition: background-color 0.2s ease;
        &:not(.ant-tabs-tab-active):not(.ant-tabs-tab-disabled):hover {
          background-color: var(--clr-general-hover);
        }
        &.ant-tabs-tab-active {
          background-color: var(--clr-general-primary);
          .ant-tabs-tab-btn {
            color: var(--clr-white);
          }
        }
        & + .ant-tabs-tab {
          margin: 0;
        }
      }
    }
    .ant-tabs-ink-bar {
      display: none;
    }
  }
`;
